/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import CensusReportTable from '../../../components/customTables/reports/patient/censusReportTable';
import { RootState } from '../../../redux/store';
import {
  clearCensusReportFilter,
  getCSVToExportSuccess,
  getDataToDownloadSuccess
} from '../../../redux/actions';
import { date } from '../../../utils/helpers';
import SearchHeader from '../../../components/searchHeader';
import {
  useGetCensusReportQuery,
  useLazyGetCensusReportDownloadQuery
} from '../../../redux/apis/Reports.api';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  CensusReportList,
  Filter
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { CReportContainer } from '../styles/ReportsViews.styles';

interface CensusReportProps {
  authorizedUser: AuthUserProps;
  selectedUnits: { id: string; unit: string }[];
  searchTerm: string;
  censusReportFilter: Filter;
  currentCensusReportPage: number;
  sortKey: string;
  sortDir: string;
  reportSearchQuery: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CensusReport: FC<CensusReportProps> = ({
  authorizedUser,
  selectedUnits,
  searchTerm,
  censusReportFilter,
  currentCensusReportPage,
  sortKey,
  sortDir,
  reportSearchQuery,
  setSearchTerm
}) => {
  const dispatch = useDispatch();
  const [triggerCensusReportExport] = useLazyGetCensusReportDownloadQuery();

  const {
    data: censusReportData,
    // isError: isGetCensusReportError,
    isFetching: isGetCensusReportFetching,
    isLoading: isGetCensusReportLoading
  } = useGetCensusReportQuery(
    {
      facilityId: authorizedUser.facilityId,
      unitId: selectedUnits.map(item => item.id),
      startday: date.formatFilterStartDate(
        censusReportFilter.startDate || date.todayDateWithDashes()
      ),
      endday: date.formatFilterEndDate(
        censusReportFilter.endDate || date.todayDateWithDashes()
      ),
      user34s: censusReportFilter.roundedBy || '',
      patient: reportSearchQuery || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      page: currentCensusReportPage,
      limit: 50,
      status: censusReportFilter.status || ''
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !authorizedUser?.facilityId
    }
  );

  useEffect(() => {
    return () => {
      dispatch(clearCensusReportFilter());
    };
  }, []);

  const listToDisplay = useMemo(() => {
    if (censusReportData?.censusReports.length > 0) {
      const cleanData = censusReportData.censusReports.map(
        (item: CensusReportList) => ({
          censusDate: date.formatDateDashtoSlash(item.reportDate),
          unit: item.unitName,
          unitId: item.unitId,
          room: item.room,
          patient: `${item.lastName}, ${item.firstName}`,
          mrn: item.mrn,
          lastRoundDate:
            item.rounds.length > 0
              ? `${date.dayOfYr(
                  item.rounds[0]?.roundDate
                )} @ ${date.localTimeOfDay(item.rounds[0].roundDate)}`
              : '',
          lastRoundedBy:
            item.rounds.length > 0
              ? `${item.rounds[0].userFirstName} ${item.rounds[0].userLastName}`
              : '',
          lastRoundedById: item.rounds.length > 0 ? item.rounds[0].userId : '',
          status:
            item.status === 'none'
              ? 'Not Rounded'
              : item.status === 'rounded'
              ? 'Rounded'
              : 'Unable to Round'
        })
      );
      return cleanData;
    }
    return [];
  }, [censusReportData, selectedUnits]);

  const exportParams = useMemo(
    () => ({
      facilityId: authorizedUser.facilityId,
      unitId: selectedUnits.map(item => item.id),
      startday: date.formatFilterStartDate(
        censusReportFilter.startDate || date.todayDateWithDashes()
      ),
      endday: date.formatFilterEndDate(
        censusReportFilter.endDate || date.todayDateWithDashes()
      ),
      user34s: censusReportFilter.roundedBy || '',
      patient: reportSearchQuery || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      status: censusReportFilter.status || ''
    }),
    [
      authorizedUser,
      selectedUnits,
      censusReportFilter,
      reportSearchQuery,
      sortKey,
      sortDir
    ]
  );

  const handleExportCsv = useCallback(() => {
    triggerCensusReportExport(exportParams, true)
      .unwrap()
      .then(data => dispatch(getCSVToExportSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  const handleClickDownloadPDF = useCallback(() => {
    triggerCensusReportExport(exportParams, true)
      .unwrap()
      .then(data => dispatch(getDataToDownloadSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  return (
    <CReportContainer id="Census-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <CensusReportTable
        censusList={listToDisplay}
        loading={isGetCensusReportFetching || isGetCensusReportLoading}
        totalTableCount={censusReportData?.totalResults}
      />
    </CReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    currentCensusReportPage: state.ReportReducer.currentCensusReportPage,
    censusReportFilter: state.ReportReducer.censusReportFilter,
    sortKey: state.ReportReducer.sortCensusKey,
    sortDir: state.ReportReducer.sortRoundsDir,
    reportSearchQuery: state.ReportReducer.reportSearchQuery,
    selectedUnits: state.ConfigReducer.selectedUnits,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(CensusReport);
